'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { SigninModal, useCurrentUser } from '@/contexts/authentication/client';
import { BookOpen, Clapperboard, Globe, Headphones, Mic, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { HeroListen } from './HeroListen';
import { HeroStoryVideo } from './HeroStoryVideo';
import { VideoFrame } from './VideoFrame';
import { staticUrl } from '@/shared/static';

/**
 * One picture, not two. The hero used to carry a strip of published gallery
 * covers under the main cover, which repeated the same idea in a smaller size —
 * and said nothing at all while the gallery was empty. The gallery has its own
 * link in the buttons above.
 */
/** Story covers our own pipeline drew, per locale; the rest fall back to art. */
const DEMO_COVERS: Record<string, string> = {
  ru: staticUrl('/landing/demo/cover-ru.webp'),
  en: staticUrl('/landing/demo/cover-en.webp'),
  cs: staticUrl('/landing/demo/cover-cs.webp'),
  de: staticUrl('/landing/demo/cover-de.webp'),
  fr: staticUrl('/landing/demo/cover-fr.webp'),
  uk: staticUrl('/landing/demo/cover-uk.webp'),
};

export default function HeroSection({
  storyVideo,
  caseFilm,
}: {
  storyVideo?: { id: string; title: string; poster?: string; kind?: 'story' | 'film' } | null;
  /** The case film of this language — a YouTube id, or a path we host ourselves
   *  before the upload. When it exists it IS the hero's picture: a film of an
   *  evening argues the job better than a cover does. The phone gets the 9:16
   *  cut when there is one, because that is where the visitors are. */
  caseFilm?: { src: string; poster: string; vertical?: string; verticalPoster?: string } | null;
} = {}) {
  const { isAuthenticated } = useCurrentUser();
  const locale = useLocale();
  const cover = DEMO_COVERS[locale] ?? staticUrl('/landing/hero-cover.webp');
  const t = useTranslations();
  const [isSigninModalOpen, setIsSigninModalOpen] = useState(false);

  return (
    <section data-testid="landing-hero-section" className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-blue-light via-background to-brand-purple-light" />
      <div className="absolute inset-0">
        <div className="absolute top-20 left-[10%] w-72 h-72 bg-brand-blue/8 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-brand-purple/8 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-pink/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20">
        {/* On a phone the FILM comes before the explanation — it is the argument,
            and a visitor who has to scroll past four paragraphs to reach it
            mostly does not. `order` only applies below lg; the desktop keeps
            text left, film right. */}
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-16">
          {/* Left: Text content. Second on a phone — see the note above. */}
          <div className="order-2 max-w-xl lg:order-1">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              {t('hero.badge')}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] mb-6">
              <span className="block">{t('hero.title.line1')}</span>
              <span className="block mt-2 bg-gradient-to-r from-brand-blue via-brand-purple to-brand-pink bg-clip-text text-transparent">
                {t('hero.title.line2')}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-4 leading-relaxed">
              {t('hero.description')}
            </p>

            {/* The three forms, named. A visitor who reads only the headline
                should still learn that the same story is a book, a narration AND
                a film — the earlier copy promised pictures and a voice, and the
                film came as a surprise on the pricing page. */}
            {/* Three forms, as three quiet pills. The same line used to be
                emoji glued together with middots, which reads as a chat message
                rather than as a product. */}
            <ul className="mb-8 flex flex-wrap gap-2" data-testid="hero-formats">
              {([
                ['read', BookOpen],
                ['listen', Headphones],
                ['watch', Clapperboard],
              ] as const).map(([key, Icon]) => (
                <li
                  key={key}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 text-sm font-medium text-foreground/80"
                >
                  <Icon className="size-4 text-primary" aria-hidden />
                  {t(`hero.formatList.${key}`)}
                </li>
              ))}
            </ul>

            {/* One button that acts, one quiet way to look around. The second
                door used to be an outline button of the same size, which made
                two equal offers out of one decision. */}
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              {isAuthenticated ? (
                <Button
                  size="lg"
                  variant="gradient-blue"
                  className="shadow-xl text-lg px-8"
                  asChild
                >
                  <Link href={`/${locale}/create?job=hero`}>
                    <Sparkles className="h-5 w-5" />
                    {t('hero.playNow')}
                  </Link>
                </Button>
              ) : (
                <Button
                  data-testid="landing-hero-get-started-button"
                  size="lg"
                  variant="gradient-blue"
                  onClick={() => setIsSigninModalOpen(true)}
                  className="shadow-xl text-lg px-8"
                >
                  <Sparkles className="h-5 w-5" />
                  {t('hero.getStarted')}
                </Button>
              )}

              {/* TWO doors, not three. A visitor who does not want to make anything
                  yet should find the finished stories here — "listen to one now" is
                  the answer to "this looks like work". The about page keeps its link
                  in the nav and the footer. */}
              {/* Down the page, not off it: the finished stories are a section of
                  this landing and they play where they stand. Sending the press to
                  /gallery traded "listen to one" for a list, and lost the page the
                  visitor was deciding on. */}
              <Link
                href="#stories"
                data-testid="hero-gallery-link"
                className="inline-flex items-center gap-2 text-base font-medium text-foreground/80 underline-offset-4 hover:text-foreground hover:underline"
              >
                <Headphones className="h-4 w-4" />
                {t('hero.gallery')}
              </Link>
            </div>

            {/* The two objections that stop a press: a card and a commitment. */}
            <p className="mt-3 mb-10 text-sm text-muted-foreground" data-testid="hero-no-card">
              {t('hero.noCard')}
            </p>

            {/* What a parent weighs before pressing. On a page selling ONE job
                the other jobs' arguments ("six languages", "a parent's voice")
                are noise, so only the objection of this one stays. */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="size-4 text-success" aria-hidden />
                {t('hero.trust.safe')}
              </span>
              {/* Who is at the keyboard. A visitor (and anyone reviewing the
                  site) should not have to guess whether a child signs in here:
                  the account is the parent's, and the child is read to. */}
              <span className="inline-flex items-center gap-2">
                <Users className="size-4 text-brand-purple" aria-hidden />
                {t('hero.trust.parent')}
              </span>
              {!caseFilm && (
                <>
                  <span className="inline-flex items-center gap-2">
                    <Globe className="size-4 text-info" aria-hidden />
                    {t('hero.trust.languages')}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Mic className="size-4 text-brand-purple" aria-hidden />
                    {t('hero.trust.voice')}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Right: the promise, shown rather than described — the photo a
              parent uploads and the cover our pipeline drew from it, side by
              side. Locales whose demo story does not exist yet fall back to the
              painted hero art. */}
          {/* Wider than a cover needs to be: the film IS the argument, and at
              max-w-md it read as a thumbnail next to the headline. */}
          {/* A vertical film is tall, not wide: the column is capped so it does
              not tower over the headline on a desktop. */}
          <div className="relative order-1 mx-auto w-full max-w-[22rem] sm:max-w-sm lg:order-2 lg:max-w-[26rem]">
            <figure className="overflow-hidden rounded-3xl border border-border bg-card shadow-2xl">
              {/* The finished thing, right here. The case film first — an
                  evening, the photo, the book — because that is the argument
                  the page makes; then a published story of this language; and
                  for a language with neither, forty seconds of narration. */}
              {caseFilm ? (
                /* ONE cut, both places: the 9:16 film. Our visitors watch
                   vertically, and a desktop that shows a different edit means
                   two things to keep in step for a page nobody reads twice. */
                <VideoFrame
                  videoId={caseFilm.vertical ?? caseFilm.src}
                  poster={caseFilm.vertical ? (caseFilm.verticalPoster ?? caseFilm.poster) : caseFilm.poster}
                  soonLabel={t('hero.listen.watchFilm')}
                  playLabel={t('hero.listen.watchFilm')}
                  vertical={!!caseFilm.vertical}
                  className="rounded-none border-0 shadow-none"
                />
              ) : storyVideo ? (
                <HeroStoryVideo
                  videoId={storyVideo.id}
                  poster={storyVideo.poster ?? cover}
                  playLabel={t(storyVideo.kind === 'film' ? 'hero.listen.watchFilm' : 'hero.listen.watch')}
                  title={storyVideo.title}
                >
                  {/* The source photo, inset — the "before" of the pair. Shown only
                      over OUR demo cover, whose hero really was drawn from this
                      photo; over another published story's cover it would claim a
                      pairing that is not true. Held at the TOP, so the play control
                      below it has the cover's bottom strip to itself. */}
                  {!storyVideo.poster && (
                  <figcaption className="pointer-events-none absolute left-4 top-4">
                    <span className="relative block size-20 overflow-hidden rounded-2xl border-[3px] border-background shadow-xl sm:size-24">
                      <Image src={staticUrl(staticUrl("/landing/demo/child.jpg"))} alt="" fill sizes="96px" className="object-cover" />
                      <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-white">
                        {t('hero.preview.photoLabel')}
                      </span>
                    </span>
                  </figcaption>
                  )}
                </HeroStoryVideo>
              ) : (
              <HeroListen
                src={staticUrl(`/landing/demo/listen-${DEMO_COVERS[locale] ? locale : 'en'}.mp3`)}
                playLabel={t('hero.listen.play')}
                pauseLabel={t('hero.listen.pause')}
              >
              <div className="relative aspect-[4/3]">
                <Image
                  src={cover}
                  alt=""
                  fill
                  priority
                  sizes="(max-width: 1024px) 90vw, 440px"
                  className="object-cover"
                />

                {/* The source photo, inset — the "before" of the pair. */}
                <figcaption className="absolute bottom-4 left-4 flex items-end gap-3">
                  <span className="relative block size-24 overflow-hidden rounded-2xl border-[3px] border-background shadow-xl sm:size-28">
                    <Image src={staticUrl(staticUrl("/landing/demo/child.jpg"))} alt="" fill sizes="112px" className="object-cover" />
                    <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide text-white">
                      {t('hero.preview.photoLabel')}
                    </span>
                  </span>
                  <span className="mb-1 rounded-full bg-background/90 px-3 py-1.5 text-xs font-semibold shadow-lg backdrop-blur">
                    {t('hero.preview.childName')}
                    <span className="block font-normal text-muted-foreground">
                      {t('hero.preview.childRole')}
                    </span>
                  </span>
                </figcaption>
              </div>
              </HeroListen>
              )}

              {/* The "mother's voice" strip belongs to the COVER preview — under
                  a film it is a second card bolted to the first, and it says
                  something the film has already said out loud. */}
              {!caseFilm && (
                <div className="flex items-center gap-3 border-t border-border p-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-blue to-brand-purple text-white">
                    <Mic className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold">{t('hero.preview.voice')}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {t('hero.preview.voiceSubtitle')}
                    </span>
                  </span>
                </div>
              )}
            </figure>
            {/* The promise in words, under the art rather than across it. */}
            {!caseFilm && (
              <p className="mt-3 text-center text-sm text-muted-foreground">
                {storyVideo
                  ? t(storyVideo.kind === 'film' ? 'hero.listen.watchFilmHint' : 'hero.listen.watchHint')
                  : t('hero.listen.hint')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* The job travels THROUGH the login.
          Without this the modal falls back to the current path, so a visitor
          who pressed "make a book about my child" came back from accounts to
          the landing and had to find the button again — the one thing the
          whole page exists to carry (FIRST.R2). */}
      <SigninModal
        isOpen={isSigninModalOpen}
        onClose={() => setIsSigninModalOpen(false)}
        callbackUrl={`/${locale}/create?job=hero`}
      />
    </section>
  );
}
